import { useState, useEffect } from "react"
import { X, Loader2 } from "lucide-react"
import { Button } from "../ui/button"
import { useNewsletterCampaigns } from "../../hooks/useNewsletter"

export default function NewsletterCampaignModal({ isOpen, onClose, campaign }) {
  const { createCampaign, loading } = useNewsletterCampaigns()

  const [formData, setFormData] = useState({
    subject: "",
    content: "",
    textContent: "",
    scheduledAt: ""
  })
  const [errors, setErrors] = useState({})

  useEffect(() => {
    if (isOpen && campaign) {
      setFormData({
        subject: campaign.subject || "",
        content: campaign.content || "",
        textContent: campaign.textContent || "",
        scheduledAt: campaign.scheduledAt ? new Date(campaign.scheduledAt).toISOString().slice(0, 16) : ""
      })
    } else if (isOpen && !campaign) {
      setFormData({
        subject: "",
        content: "",
        textContent: "",
        scheduledAt: ""
      })
    }
    setErrors({})
  }, [isOpen, campaign])

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }))
    setErrors((prev) => ({ ...prev, [name]: "" }))
  }

  const validateForm = () => {
    const newErrors = {}
    if (!formData.subject.trim()) {
      newErrors.subject = "Le sujet est requis"
    } else if (formData.subject.length > 200) {
      newErrors.subject = "Le sujet ne doit pas dépasser 200 caractères"
    }
    if (!formData.content.trim()) {
      newErrors.content = "Le contenu HTML est requis"
    }
    if (formData.scheduledAt && new Date(formData.scheduledAt) < new Date()) {
      newErrors.scheduledAt = "La date programmée doit être dans le futur"
    }
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!validateForm()) {
      return
    }

    try {
      await createCampaign({
        subject: formData.subject,
        content: formData.content,
        textContent: formData.textContent || undefined,
        scheduledAt: formData.scheduledAt ? new Date(formData.scheduledAt).toISOString() : undefined
      })
      onClose()
    } catch (err) {
      console.error("Erreur lors de la création de la campagne:", err)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-900">
            {campaign ? "Modifier la campagne" : "Créer une nouvelle campagne"}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
              Sujet de l'email *
            </label>
            <input
              type="text"
              id="subject"
              name="subject"
              value={formData.subject}
              onChange={handleInputChange}
              maxLength={200}
              className={`w-full px-3 py-2 border ${errors.subject ? "border-red-500" : "border-gray-300"} rounded-lg focus:ring-2 focus:ring-[#141835] focus:border-transparent`}
              disabled={loading}
              placeholder="Ex: Nouveaux cours disponibles ce mois-ci !"
            />
            {errors.subject && <p className="mt-1 text-sm text-red-600">{errors.subject}</p>}
          </div>

          <div>
            <label htmlFor="content" className="block text-sm font-medium text-gray-700 mb-2">
              Contenu HTML *
            </label>
            <textarea
              id="content"
              name="content"
              value={formData.content}
              onChange={handleInputChange}
              rows={10}
              className={`w-full px-3 py-2 border ${errors.content ? "border-red-500" : "border-gray-300"} rounded-lg focus:ring-2 focus:ring-[#141835] focus:border-transparent resize-y font-mono text-sm`}
              disabled={loading}
              placeholder="<h1>Bienvenue dans notre newsletter</h1><p>Contenu de la newsletter...</p>"
            ></textarea>
            {errors.content && <p className="mt-1 text-sm text-red-600">{errors.content}</p>}
            <p className="mt-1 text-xs text-gray-500">
              Utilisez du HTML pour formater votre newsletter. N'oubliez pas d'inclure un pixel de tracking et des liens trackés.
            </p>
          </div>

          <div>
            <label htmlFor="textContent" className="block text-sm font-medium text-gray-700 mb-2">
              Version texte (optionnel)
            </label>
            <textarea
              id="textContent"
              name="textContent"
              value={formData.textContent}
              onChange={handleInputChange}
              rows={5}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#141835] focus:border-transparent resize-y"
              disabled={loading}
              placeholder="Version texte de la newsletter pour les clients email qui ne supportent pas HTML"
            ></textarea>
            <p className="mt-1 text-xs text-gray-500">
              Version texte alternative pour les clients email qui ne supportent pas HTML
            </p>
          </div>

          <div>
            <label htmlFor="scheduledAt" className="block text-sm font-medium text-gray-700 mb-2">
              Date d'envoi programmée (optionnel)
            </label>
            <input
              type="datetime-local"
              id="scheduledAt"
              name="scheduledAt"
              value={formData.scheduledAt}
              onChange={handleInputChange}
              className={`w-full px-3 py-2 border ${errors.scheduledAt ? "border-red-500" : "border-gray-300"} rounded-lg focus:ring-2 focus:ring-[#141835] focus:border-transparent`}
              disabled={loading}
            />
            {errors.scheduledAt && <p className="mt-1 text-sm text-red-600">{errors.scheduledAt}</p>}
            <p className="mt-1 text-xs text-gray-500">
              Si non renseigné, la campagne sera créée en brouillon et pourra être envoyée manuellement
            </p>
          </div>

          <div className="flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
              Annuler
            </Button>
            <Button type="submit" className="bg-[#141835] hover:bg-[#1a1f4a] text-white" disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Enregistrement...
                </>
              ) : (
                campaign ? "Mettre à jour" : "Créer la campagne"
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
